import React, { useRef, useState } from 'react';
import { Box, Typography, CardMedia, Avatar } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Cross from '../../img-offer/Cross.svg';
import EditIcon from '@mui/icons-material/Edit';

export default function ReusableImageComponent({
  selectedFile,
  selectedFiles,
  setSelectedFile,
  setSelectedFiles,
  handleFileChange,
  multiple = false,
  disabled = false,
  imageAlt = 'Uploaded Image',
  tooltipTitle = "Update image",
  errorMsg,
  caption,
  error,
  helperText,
  onDelete ,
  onEdit,
  hideDeleteIcon = false,
  hideEditIcon = false,
  uploadDisabled = false, 
  editDisabled = false, 
}) {
  const fileInputRef = useRef(null);
  const editInputRefs = useRef([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const triggerFileInput = () => {
    if (!uploadDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileDelete = (index) => {
    if (multiple && setSelectedFiles) {
      const updatedFiles = [...selectedFiles];
      updatedFiles.splice(index, 1);
      setSelectedFiles(updatedFiles);
    } else if (!multiple && setSelectedFile) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleFileInputChange = (event) => {
    const files = event.target.files;
    handleFileChange({ target: { files } });
    event.target.value = null;
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    if (uploadDisabled) return;

    const files = event.dataTransfer.files;
    handleFileChange({ target: { files } });
  };

  const handleEditFileChange = (event, index) => {
    const file = event.target.files[0];
    if (!file) return;

    if (multiple && selectedFiles && setSelectedFiles) {
      const updatedFiles = [...selectedFiles];
      updatedFiles[index] = file;
      setSelectedFiles(updatedFiles);
    } else if (!multiple && setSelectedFile) {
      setSelectedFile(file);
    }

    if (onEdit) {
      onEdit(index, file);
    }

    event.target.value = null;
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!uploadDisabled) setIsDragOver(true);
  };

  const handleDragLeave = () => {
    if (!uploadDisabled) setIsDragOver(false);
  };

  const previewUrls = multiple
    ? (selectedFiles || []).map(file => (file instanceof File ? URL.createObjectURL(file) : file))
    : selectedFile
      ? [selectedFile instanceof File ? URL.createObjectURL(selectedFile) : selectedFile]
      : [];

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        onClick={triggerFileInput}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          border: error ? '2px dashed #d32f2f' : isDragOver ? '2px dashed #1473FD' : '2px dashed rgba(0,0,0,0.2)',
          borderRadius: '8px',
          padding: '3rem',
          textAlign: 'center',
          cursor: uploadDisabled ? 'not-allowed' : 'pointer',
          backgroundColor: isDragOver ? '#e3f2fd' : '#f9f9f9',
          transition: 'background-color 0.2s, border 0.2s',
        }}
      >
        <input
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleFileInputChange}
          ref={fileInputRef}
          disabled={uploadDisabled}
          style={{ display: 'none' }}
        />
        <UploadFileIcon fontSize="large" color="primary" />
        <Typography variant="body2" color="#1473FD" sx={{ mt: 1, fontSize: '16px' }}>
          Click to upload or drag and drop
        </Typography>
        <Typography variant="caption" color="#A6A6A6" sx={{ fontSize: '14px' }}>
          {caption}
        </Typography>
        <br />
        <Typography variant="caption" color="#7A7A7A" sx={{ mt: 0.5, fontSize: '14px' }}>
          {errorMsg}
        </Typography>
      </Box>

      {previewUrls.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {previewUrls.map((src, index) => (
            <Box key={index} sx={{ position: 'relative', display: 'inline-block' }}>
              <CardMedia
                component="img"
                image={src}
                alt={`${imageAlt} ${index + 1}`}
                sx={{
                  borderRadius: '10px',
                  backgroundColor: '#F9F9F9',
                  height: '92px',
                  width: '111px',
                  border: '1px solid #A6A6A6',
                  objectFit: 'cover',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '5px',
                  right: '7px',
                  display: 'flex',
                  gap: 0.5,
                  padding: '2px',
                }}
              >
                {!hideEditIcon && (
                  <>
                    <Avatar
                      sx={{
                        width: 20,
                        height: 20,
                        cursor: 'pointer',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 0.3,
                      }}
                      onClick={() => {
                        if (editInputRefs.current[index]) {
                          editInputRefs.current[index].click();
                        }
                      }}
                      disabled={editDisabled}
                    >
                      <EditIcon sx={{ fontSize: 14, color: '#1976d2' }} />
                    </Avatar>
                    <input
                      type="file"
                      accept="image/*"
                      ref={(el) => (editInputRefs.current[index] = el)}
                      style={{ display: 'none' }}
                      onChange={(e) => handleEditFileChange(e, index)}
                      disabled={editDisabled}
                    />
                  </>
                )}

                {!hideDeleteIcon && (
                  <Avatar
                    src={Cross}
                    onClick={() => {
                      if (onDelete) {
                        onDelete(index);
                      } else {
                        handleFileDelete(index);
                      }
                    }}
                    sx={{
                      width: 20,
                      height: 20,
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
